import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { Fmt } from 'src/app/core/messages/fmt';
import { Permission } from 'src/app/core/resources/permission';
import { Utils } from 'src/app/core/resources/utils';
import { MenuServiceService } from '../../menu-service/menu-service.service';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { PermissionRole } from 'src/app/core/resources/permission-role';
import { ModelCargo } from 'src/app/modules/cargo/manual-creation-cargo/resources/modelCargo';
import { ThirdPartyConsignmentService } from 'src/app/modules/cargo/manual-creation-cargo/components/third-party-consignment/third-party-consignment.service';
import { ConsignmentCityOption } from 'src/app/shared/consignment-card/consignment-card.component';
import { CargoDetailService } from 'src/app/modules/cargo/cargo-detail/cargo-detail.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ConsignmentCargo } from 'src/app/core/interfaces/consignmentCargo';
import { AmountsCargoEnum } from 'src/app/core/enums/amountsCargo.enum';
import { Global } from 'src/app/core/resources/global';
import { ServiceResources } from '../../resources/services';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { AddressContainer } from 'src/app/core/interfaces/addressCargo';
import { CargoManager } from 'src/app/core/managers/cargo.manager';
import { ConsignmentManager } from 'src/app/core/managers/consignment.manager';
import {
  DOCUMENT_RECREATION_IN_PROGRESS_MESSAGE,
  DOCUMENT_RECREATION_IN_PROGRESS_TITLE,
  isDocumentRecreationInProgress
} from 'src/app/core/resources/transport-company-change.utils';

@Component({
  selector: 'app-consignments-service-form',
  templateUrl: './consignments-service-form.component.html',
  styleUrls: ['./consignments-service-form.component.scss'],
  providers: [ModelCargo, ConsignmentManager]
})
export class ConsignmentsServiceFormComponent implements OnInit {
  permission = Permission;
  formSubscription: Subscription;
  manifestType = new FormControl('', Validators.required);
  operationType = new FormControl('');
  manifestDailyTripsCount = new FormControl('', [Validators.required, Validators.min(1)]);
  readonly operationTypeOptions: { code: string, description: string, tooltip: string }[] = this.utils.clone(this.menuService.operationTypes);
  manifestTypeOptions: { code: string, description: string, tooltip: string }[] = [];
  senderCityOptions: { [key: string]: ConsignmentCityOption } = {};
  recipientCityOptions: { [key: string]: ConsignmentCityOption } = {};
  originalConsignments: ConsignmentCargo[] = [];
  consignmentsFormArray: FormArray = new FormArray([]);
  consignmentFieldsToCheck: { field: string, alias: string }[] = this.global.consignmentFieldsToCheck;
  formValidate = '';
  numberOfConsignmentsShowed = 5;
  isDialog = false;
  monitoringCompanyControl = new FormControl(null, [Validators.required]);
  initialMonitoringValue: { id: string, name: string } | null = null;
  readonly documentRecreationInProgressMessage = DOCUMENT_RECREATION_IN_PROGRESS_MESSAGE;
  readonly documentRecreationInProgressTitle = DOCUMENT_RECREATION_IN_PROGRESS_TITLE;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public paramsDialog: { cargo: Cargo },
    public dialogRef: MatDialogRef<ConsignmentsServiceFormComponent>,
    public menuService: MenuServiceService,
    private fb: FormBuilder,
    private global: Global,
    private spinner: NgxSpinnerService,
    private router: Router,
    private snackBarService: SnackBarService,
    private permissionRole: PermissionRole,
    public utils: Utils,
    private cargoManager: CargoManager,
    private thirdPartyConsignmentService: ThirdPartyConsignmentService,
    private cargoDetailService: CargoDetailService,
    private serviceResources: ServiceResources,
    public consignmentManager: ConsignmentManager,
  ) { }

  async ngOnInit() {
    await this.getManifestTypeOptions();
    await this.checkDialogParams();
    this.setLocalValues();
    this.setSubscription();
  }

  private async getManifestTypeOptions() {
    const tooltips = {
      'G': 'Viaje estandar de origen a destino. Requiere remesa para cada parada.',
      'M': "Viaje nacional que tiene paradas en municipios intermedios distintos entre el origen y el destino con al menos dos remesas.",
      "I": "Operación con dos trayectos ida y vuelta. Entre dos municipios distintos y con al menos dos remesas.",
      "W": "Viaje vacío. No requiere remesa. Se usa para pagar el viaje vacío en cualquier vía nacional.",
      "D": "Cubre varios viajes en un mismo día para un mismo vehículo entre un único origen y un único destino",
      "U": "Viaje urbano o dentro del mismo municipio"
    };
    try {
      const manifestTypes = await this.cargoDetailService.getManifestTypes().toPromise();
      if (manifestTypes && manifestTypes.catalog && manifestTypes.catalog.length) {
        this.manifestTypeOptions = manifestTypes.catalog.map(item => ({
          code: item.id,
          description: item.name,
          tooltip: tooltips[item.id]
        }));
      }
      else this.manifestTypeOptions = [];
    }
    catch (error) {
      console.error(error);
      this.manifestTypeOptions = [];
    }
  }

  private async checkDialogParams() {
    if (this.paramsDialog && this.paramsDialog.cargo) {
      this.isDialog = true;
      this.menuService.cargo = this.paramsDialog.cargo;
      this.menuService.patchForm(this.paramsDialog.cargo, false);
      await this.menuService.setCargoConsignments();
      await this.menuService.setCargoVehicle();
    }
  }

  private setLocalValues() {
    if (!this.menuService.isRequiredConsignmentsService()) return this.continueToNextStep();
    const manifestType = this.menuService.form.get('manifestType').value;
    this.initialMonitoringValue = this.utils.getNestedValue(this.menuService.form.value, 'monitoringCompany');
    const manifestTypeOption = this.manifestTypesFiltered.find(option => manifestType && option.code === manifestType.code);
    this.manifestType.setValue(manifestTypeOption ? manifestTypeOption : '');
    const operationType = this.menuService.form.get('cargoModel.operationType').value;
    const operationTypeOption = this.operationTypeOptions.find(option => operationType && option.code === operationType.code);
    this.operationType.setValue(operationTypeOption ? operationTypeOption : '');
    const manifestDailyTripsCountControl = this.menuService.form.get('manifestDailyTripsCount');
    const manifestDailyTripsCount = manifestDailyTripsCountControl ? manifestDailyTripsCountControl.value : 0;
    this.manifestDailyTripsCount.setValue(manifestDailyTripsCount || 0);
    const isManifestTypeD = this.manifestType.value && this.manifestType.value.code === 'D';
    if (isManifestTypeD) {
      this.manifestDailyTripsCount.setValidators([Validators.required, Validators.min(1)]);
      if (this.canEditConsignmentServiceModule && !this.documentRecreationInProgress) {
        this.manifestDailyTripsCount.enable();
      }
    } else {
      this.manifestDailyTripsCount.clearValidators();
    }
    this.numberOfConsignmentsShowed = 5;
    this.setCityOptions();
    this.checkPermissionsToEdit();
    this.setConsignments();
  }

  private setSubscription() {
    this.formSubscription = this.menuService.formObservable.subscribe(() => this.setLocalValues());
    this.manifestType.valueChanges.subscribe(() => {
      const isManifestTypeD = this.manifestType.value && this.manifestType.value.code === 'D';
      if (isManifestTypeD) {
        this.manifestDailyTripsCount.setValidators([Validators.required, Validators.min(1)]);
        if (this.canEditConsignmentServiceModule && !this.documentRecreationInProgress) {
          this.manifestDailyTripsCount.enable();
        }
      } else {
        this.manifestDailyTripsCount.clearValidators();
        this.manifestDailyTripsCount.setValue('');
      }
      this.manifestDailyTripsCount.updateValueAndValidity();
    });
  }

  private setCityOptions() {
    const { sender, destination } = this.cargoManager.getSenderAndDestinationConsignmentOptions(this.menuService.form.value);
    this.senderCityOptions = sender;
    this.recipientCityOptions = destination;
  }

  private checkPermissionsToEdit() {
    if (!this.canEditConsignmentServiceModule || this.documentRecreationInProgress) {
      this.manifestType.disable();
      this.operationType.disable();
      this.monitoringCompanyControl.disable();
      this.manifestDailyTripsCount.disable();
      this.formValidate = 'disable';
    } else if (!this.canEditConsignments) {
      this.consignmentsFormArray.disable();
      this.formValidate = 'disable';
    } else {
      this.manifestType.enable();
      this.operationType.enable();
      this.monitoringCompanyControl.enable();
      if (this.manifestType.value && this.manifestType.value.code === 'D')
        this.manifestDailyTripsCount.enable();
      this.consignmentsFormArray.enable();
      this.formValidate = 'enable';
    }
  }

  private setConsignments() {
    this.cargoDetailService.getCargoConsignments(this.menuService.cargo.id, 'Created').subscribe(
      (consignments: ConsignmentCargo[]) => {
        if (consignments && this.utils.deepEqual(consignments, this.originalConsignments)) return;
        this.consignmentsFormArray.clear();
        this.originalConsignments = [];
        if (consignments && consignments.length) {
          this.originalConsignments = consignments;
          consignments.forEach(consignment => {
            const form = this.menuService.getConsignmentForm();
            form.patchValue(consignment);
            form.get('cargoOwner').patchValue(this.menuService.form.get('cargoOwner').value);
            this.consignmentsFormArray.push(form);
          });
        }
      }, () => {
        this.consignmentsFormArray.clear();
        this.originalConsignments = [];
      }
    );
  }

  get manifestTypesFiltered(): { code: string, description: string, tooltip: string }[] {
    if (!this.manifestTypeOptions.length) return [];
    if (!this.menuService.cargo) return [];
    const isUrbanService = this.utils.isSameCity(this.menuService.cargo);
    const urbanManifestTypes = this.menuService.urbanManifestTypes;
    const nationalManifestTypes = this.menuService.nationalManifestTypes;
    if (isUrbanService) return this.manifestTypeOptions.filter(option => urbanManifestTypes.includes(option.code));
    return this.manifestTypeOptions.filter(option => nationalManifestTypes.includes(option.code));
  }

  addConsignmentForm() {
    if (this.blockDocumentMutation()) return;
    this.consignmentsFormArray.push(this.menuService.getConsignmentForm());
    if (this.numberOfConsignmentsShowed < this.consignmentsFormArray.length)
      this.watchMoreConsignments();
  }

  removeConsignmentForm(index: number) {
    if (this.blockDocumentMutation()) return;
    this.consignmentsFormArray.removeAt(index);
  }

  get filteredConsignmentsFormArray(): AbstractControl[] {
    if (!this.consignmentsFormArray.length) return [];
    if (this.consignmentsFormArray.length <= this.numberOfConsignmentsShowed)
      return this.consignmentsFormArray.controls;
    return this.consignmentsFormArray.controls.slice(0, this.numberOfConsignmentsShowed);
  }

  watchMoreConsignments() {
    this.numberOfConsignmentsShowed += 5;
  }

  getTitleConsignment(consignment: ConsignmentCargo, index: number): string {
    if (consignment.id) return `Remesa ${index + 1} (${consignment.id})`;
    return `Remesa ${index + 1} (Nueva)`;
  }

  compareByCode(a: { code: string }, b: { code: string }) {
    return a && b && a.code === b.code;
  }

  get canShowConsignmentServiceModule(): boolean {
    return this.permissionRole.hasPermission(
      this.permission.cargo.module,
      this.permission.cargo.showConsignmentServiceModule
    );
  }

  get canEditConsignmentServiceModule(): boolean {
    return this.canShowConsignmentServiceModule && this.permissionRole.hasPermission(
      this.permission.cargo.module,
      this.permission.cargo.editConsignmentServiceModule
    );
  }

  get canEditConsignments(): boolean {
    return this.canEditConsignmentServiceModule
      && !this.documentRecreationInProgress
      && this.menuService.isValidContractorTripInfo().isValid;
  }

  backToPreviousStep() {
    this.router.navigate([Fmt.string(this.serviceResources.serviceRequestUrls.observationsService, this.menuService.cargo.consecutive)]);
  }

  private areConsignmentsValid(): boolean {
    for (const [index, consignmentForm] of this.consignmentsFormArray.controls.entries()) {
      for (const consignmentField of this.consignmentFieldsToCheck) {
        if (this.utils.errorMessagesCustomized(consignmentForm.get(consignmentField.field), `${consignmentField.alias} de la remesa ${index + 1}`))
          return false;
      }
      const isValidByVariableFields = this.consignmentManager.getCompleteVariableFields(
        consignmentForm.value as ConsignmentCargo,
        this.menuService.cargo
      );
      if (!isValidByVariableFields.valid) {
        this.snackBarService.openSnackBar(`Error en la remesa ${index + 1}: ${isValidByVariableFields.error}`, undefined, 'alert');
        return false;
      }
      const isValidByDangerousFields = this.consignmentManager.getCompleteDangerousFields(consignmentForm.value as ConsignmentCargo);
      if (!isValidByDangerousFields.valid) {
        this.snackBarService.openSnackBar(`Error en la remesa ${index + 1}: ${isValidByDangerousFields.error}`, undefined, 'alert');
        return false;
      }
    }
    const { duplicated, duplicatedConsignments } = this.cargoManager.verifyDuplicatedConsignments(this.consignmentsFormArray.value);
    if (duplicated) {
      this.snackBarService.openSnackBar(`Las remesas ${duplicatedConsignments.map(c => c.id ? c.id : 'la nueva remesa').join(' y ')} son iguales`, undefined, 'alert');
      return false;
    }
    return true;
  }

  private isFormValid(): boolean {
    if (this.utils.errorMessagesCustomized(this.manifestType, 'tipo de manifiesto')) return false;
    const isManifestTypeD = this.manifestType.value && this.manifestType.value.code === 'D';
    if (isManifestTypeD) this.manifestDailyTripsCount.markAsTouched();
    if (isManifestTypeD && this.utils.errorMessagesCustomized(this.manifestDailyTripsCount, 'número de viajes')) return false;
    if (this.utils.errorMessagesCustomized(this.monitoringCompanyControl, 'empresa de monitoreo')) return false;
    return true;
  }

  async saveAndContinue() {
    if (this.blockDocumentMutation()) return;
    this.formValidate = 'touched';
    if (!this.isFormValid()) return;
    if (!this.isDialog && !this.areConsignmentsValid()) return;
    let consignments: ConsignmentCargo[] = [];
    this.consignmentsFormArray.controls.forEach((consignment: FormGroup) => {
      consignments.push(this.utils.clone(consignment.getRawValue()) as ConsignmentCargo);
    });
    let totalWeigth = 0;
    const monitoringCompany = this.monitoringCompanyControl.value;
    const manifestType = this.manifestType.value;
    const operationType = this.operationType.value;
    const manifestDailyTripsCount = this.manifestDailyTripsCount.value;

    if (consignments && consignments.length)
      totalWeigth = consignments.reduce((acc, consignment) => acc + Number(consignment.totalWeight), 0);
    if (!this.isDialog && consignments && this.canEditConsignments) {
      this.spinner.show();
      try {
        const { success, error } = await this.processConsignments(consignments);
        if (!success) return this.snackBarService.openSnackBar(error ? error : 'Ocurrió un error al guardar las remesas', undefined, 'error');
        await this.menuService.refreshCargo();
        consignments = await this.cargoDetailService.getCargoConsignments(this.menuService.cargo.id, 'Created').toPromise();
      } catch (error) {
        return this.snackBarService.openSnackBar('Ocurrió un error al recargar las remesas guardadas', undefined, 'error');
      } finally {
        this.spinner.hide();
      }
    }

    if (totalWeigth) {
      this.menuService.form.get('cargoFeature.cargoMeasure.totalWeigth').setValue(
        this.menuService.isEmptyContainer() ? 0 : totalWeigth <= AmountsCargoEnum.MAX_WEIGHT_ALLOWED_KG
          ? totalWeigth : AmountsCargoEnum.MAX_WEIGHT_ALLOWED_KG
      );
      this.menuService.form.get('cargoFeature.cargoMeasure.unit').setValue("1");
    }

    if (!this.menuService.form.get('monitoringCompany')) {
      this.menuService.form.addControl('monitoringCompany', new FormGroup({
        id: new FormControl(''),
        name: new FormControl('')
      }))
    }
    this.menuService.form.get('monitoringCompany').patchValue(monitoringCompany);
    this.menuService.form.get('manifestType').patchValue({
      code: manifestType && manifestType.code ? manifestType.code : '',
      description: manifestType && manifestType.description ? manifestType.description : ''
    });
    this.menuService.form.get('cargoModel.operationType').patchValue({
      code: operationType && operationType.code ? operationType.code : '',
      description: operationType && operationType.description ? operationType.description : ''
    });
    const isManifestTypeD = manifestType && manifestType.code === 'D';
    const manifestDailyTripsCountValue = manifestDailyTripsCount ? Number(manifestDailyTripsCount) : null;
    if (isManifestTypeD && manifestDailyTripsCountValue) {
      if (!this.menuService.form.get('manifestDailyTripsCount'))
        this.menuService.form.addControl('manifestDailyTripsCount', new FormControl(''));
      this.menuService.form.get('manifestDailyTripsCount').setValue(manifestDailyTripsCountValue);
    }
    this.setAddressContainersFromConsignments(consignments);
    const containers: string[] = consignments
      .map((consignment) => consignment.container)
      .filter((containerNumber) => !!containerNumber);
    this.menuService.form.get('containers').setValue(containers);
    try { await this.menuService.updateService() } catch (error) {
      return this.snackBarService.openSnackBar('Ocurrió un error al actualizar el servicio', undefined, 'error');
    }

    this.snackBarService.openSnackBar("Servicio actualizado exitosamente");
    if (!this.isDialog) this.continueToNextStep();
    else this.dialogRef.close({ state: true });
  }

  private async processConsignments(consignments: ConsignmentCargo[]): Promise<{ success: boolean, error?: string }> {
    const consignmentsToAdd = consignments.filter((consignment) => !consignment.id);
    const consignmentsToRemove = this.originalConsignments.filter((consignment) => !consignments.some((savedConsignment) => savedConsignment.id === consignment.id));
    const consignmentsToUpdate = consignments.filter((consignment) => consignment.id).filter((consignment) => {
      const originalConsignment = this.originalConsignments.find((originalConsignment) => originalConsignment.id === consignment.id);
      return !this.utils.deepEqual(originalConsignment, consignment);
    }).filter(consignment => !consignment.entryId);
    const promises = [];
    if (consignmentsToAdd.length) {
      const consignmentsToCreate = this.checkConsignments(consignmentsToAdd);
      promises.push(this.createConsignments(consignmentsToCreate));
    }
    if (consignmentsToUpdate.length) {
      const consignmentsToChange = this.checkConsignments(consignmentsToUpdate);
      promises.push(this.updateConsignments(consignmentsToChange));
    }
    if (consignmentsToRemove.length) promises.push(this.deleteConsignments(consignmentsToRemove));
    if (!promises.length) return { success: true };
    try {
      await Promise.all(promises);
      return { success: true };
    } catch (error) {
      if (error && error.error && error.error.message) return { success: false, error: error.error.message };
      return { success: false };
    }
  }


  private async createConsignments(consignmentsToCreate: ConsignmentCargo[]): Promise<any> {
    return this.cargoDetailService.createConsignment(consignmentsToCreate).toPromise();
  }

  private async updateConsignments(consignments: ConsignmentCargo[]): Promise<any> {
    return this.cargoDetailService.updateConsignments(consignments).toPromise();
  }

  private async deleteConsignments(consignments: ConsignmentCargo[]): Promise<any> {
    return consignments.map((consignment) => {
      return this.cargoDetailService.deleteConsignment(consignment.documentId, this.menuService.cargo.id).toPromise();
    });
  }

  private checkConsignments(consignments: ConsignmentCargo[]): ConsignmentCargo[] {
    const consignmentsToCheck: ConsignmentCargo[] = this.utils.clone(consignments);
    for (const consignment of consignmentsToCheck) {
      const cargoOwner = this.utils.getNestedValue(this.menuService.cargo, 'cargoOwner');
      const consignmentCargoOwner = this.utils.getNestedValue(consignment, 'cargoOwner');
      if (cargoOwner && consignmentCargoOwner) {
        consignment.cargoOwner.branchOffice = consignmentCargoOwner.branchOffice === cargoOwner.branchOffice ? consignmentCargoOwner.branchOffice : cargoOwner.branchOffice;
        consignment.cargoOwner.documentNumber = consignmentCargoOwner.documentNumber === cargoOwner.documentNumber ? consignmentCargoOwner.documentNumber : cargoOwner.documentNumber;
        consignment.cargoOwner.documentType = consignmentCargoOwner.documentType === cargoOwner.documentType ? consignmentCargoOwner.documentType : cargoOwner.documentType;
        consignment.cargoOwner.name = consignmentCargoOwner.name === cargoOwner.name ? consignmentCargoOwner.name : cargoOwner.name;
      }
      if (consignment.errorCreation) consignment.errorCreation = null;
      if (consignment.errorFinalFulfillment) consignment.errorFinalFulfillment = null;
      if (consignment.errorCancellation) consignment.errorCancellation = null;
      if (consignment.errorInitialFulfillment) consignment.errorInitialFulfillment = null;
      if (consignment.errorCancellation) consignment.errorCancellation = null;
      if (!consignment.requiresSubSection) delete consignment.subSection;
      if (!consignment.requiresTaxCode) delete consignment.tax;
      if (!consignment.isDangerous) delete consignment.dangerousMerchandise;
      if (!consignment.isHazardousWaste) delete consignment.hazardousWaste;
      if (!this.menuService.isContainerService()) delete consignment.container;
      if (!this.consignmentManager.consignmentRequiresDescription(consignment)) delete consignment.productDescription;
      if (!this.consignmentManager.consignmentRequiresConsolidationType(consignment)) delete consignment.consolidationType;
      if (!this.consignmentManager.consignmentRequiresAmountProduct(consignment)) {
        delete consignment.amountProduct;
        delete consignment.unitMeasureProduct;
      }
    }
    return consignmentsToCheck;
  }

  private setAddressContainersFromConsignments(consignments: ConsignmentCargo[]): void {
    const destinations = this.menuService.form.get('cargoFeature.uploadDownload.destination') as FormArray;
    if (!destinations) {
      return;
    }
    destinations.controls.forEach((destinationGroup) => {
      const addresses = destinationGroup.get('addresses') as FormArray;
      if (!addresses) {
        return;
      }
      addresses.controls.forEach((addressGroup) => {
        const linkedConsignmentIds: string[] = addressGroup.get('consignments')
          ? addressGroup.get('consignments').value
          : [];
        const addressContainers: AddressContainer[] = (linkedConsignmentIds || [])
          .map((consignmentId) => {
            const matchedConsignment = consignments.find((consignment) => consignment.id === consignmentId);
            return matchedConsignment && matchedConsignment.container
              ? { consignmentId, container: matchedConsignment.container }
              : null;
          })
          .filter((addressContainer) => !!addressContainer);
        if (!addressGroup.get('containers')) {
          (addressGroup as FormGroup).addControl('containers', new FormControl([]));
        }
        addressGroup.get('containers').setValue(addressContainers);
      });
    });
  }

  continueToNextStep() {
    this.router.navigate([Fmt.string(this.serviceResources.serviceRequestUrls.servicePublication, this.menuService.cargo.consecutive)]);
  }

  get documentRecreationInProgress(): boolean {
    return isDocumentRecreationInProgress(this.menuService.cargo);
  }

  private blockDocumentMutation(): boolean {
    if (!this.documentRecreationInProgress) return false;
    this.snackBarService.openSnackBar(
      this.documentRecreationInProgressMessage,
      undefined,
      'alert'
    );
    return true;
  }

  ngOnDestroy() {
    if (this.formSubscription) this.formSubscription.unsubscribe();
  }

}
